/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { getAccountTrades } from '../../api/api';
import { useAccountData } from '../../context/AccountDataProvider';
import { useDateTimeFormatting } from '../../utils/datetime';

/**
 * ClosedPositionsWidget - Displays closed/historical trades
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - accountId: string - filter by account (uses context if not provided)
 * - limit: number - max trades to display
 */
const ClosedPositionsWidget = ({ 
  hideTitle = false,
  accountId: propAccountId = null,
  limit = 50
}) => {
  const { selectedAccountId } = useAccountData();
  const { formatDateTime } = useDateTimeFormatting();
  const accountId = propAccountId || selectedAccountId;
  
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const fetchTrades = useCallback(async () => {
    if (!accountId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getAccountTrades(accountId, { status: 'closed', limit });
      const tradesArray = Array.isArray(data) ? data : (data?.results || data?.trades || []);
      // Filter only closed trades
      const closedTrades = tradesArray.filter(t => 
        t.trade_status === 'closed' || t.status === 'closed'
      );
      setTrades(closedTrades);
    } catch (err) {
      console.error('Error fetching closed trades:', err);
      setError('Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [accountId, limit]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  const sortedTrades = useMemo(() => {
    const sorted = [...trades].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      
      if (sortKey === 'created_at' || sortKey === 'closed_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [trades, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (formatDateTime) {
      return formatDateTime(dateStr, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toISOString();
  };

  const formatPnL = (pnl) => {
    const num = parseFloat(pnl);
    if (isNaN(num)) return '-';
    const formatted = num.toFixed(2);
    return num >= 0 ? `+$${formatted}` : `-$${Math.abs(num).toFixed(2)}`;
  };

  const getPnLColor = (pnl) => {
    const num = parseFloat(pnl);
    if (isNaN(num)) return 'text-textSecondary';
    return num >= 0 ? 'text-success' : 'text-destructive';
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return null;
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <Widget title="Closed Positions" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-borderDefault">
          <span className="text-xs text-textSecondary">
            {trades.length} closed trades
          </span>
          <button
            onClick={fetchTrades}
            disabled={loading}
            className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {!accountId && (
          <div className="p-4 text-center text-textSecondary text-sm">
            Select an account to view closed positions
          </div>
        )}

        {accountId && error && (
          <div className="p-4 text-center text-destructive text-sm">{error}</div>
        )}

        {accountId && !error && trades.length === 0 && !loading && (
          <div className="p-4 text-center text-textSecondary text-sm">No closed trades</div>
        )}

        {accountId && !error && trades.length > 0 && (
          <div className="flex-1 overflow-auto -mx-2 sm:mx-0">
            <table className="w-full min-w-[400px] text-[10px] sm:text-xs">
              <thead className="sticky top-0 bg-backgroundSurface">
                <tr className="text-textSecondary border-b border-borderDefault">
                  <th 
                    className="py-1.5 sm:py-2 px-1 sm:px-2 text-left cursor-pointer hover:text-textPrimary"
                    onClick={() => handleSort('instrument')}
                  >
                    Symbol<SortIcon column="instrument" />
                  </th>
                  <th 
                    className="py-1.5 sm:py-2 px-1 sm:px-2 text-left cursor-pointer hover:text-textPrimary"
                    onClick={() => handleSort('direction')}
                  >
                    Side<SortIcon column="direction" />
                  </th>
                  <th 
                    className="py-1.5 sm:py-2 px-1 sm:px-2 text-right cursor-pointer hover:text-textPrimary hidden sm:table-cell"
                    onClick={() => handleSort('lot_size')}
                  >
                    Lots<SortIcon column="lot_size" />
                  </th>
                  <th className="py-1.5 sm:py-2 px-1 sm:px-2 text-right hidden sm:table-cell">Entry</th>
                  <th className="py-1.5 sm:py-2 px-1 sm:px-2 text-right hidden sm:table-cell">Exit</th>
                  <th 
                    className="py-1.5 sm:py-2 px-1 sm:px-2 text-right cursor-pointer hover:text-textPrimary"
                    onClick={() => handleSort('actual_profit_loss')}
                  >
                    P/L<SortIcon column="actual_profit_loss" />
                  </th>
                  <th 
                    className="py-1.5 sm:py-2 px-1 sm:px-2 text-right cursor-pointer hover:text-textPrimary hidden sm:table-cell"
                    onClick={() => handleSort('created_at')}
                  >
                    Closed<SortIcon column="created_at" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map((trade, idx) => (
                  <tr 
                    key={trade.id || trade.trade_id || idx} 
                    className="border-b border-borderDefault/50 hover:bg-secondary/10"
                  >
                    <td className="py-1.5 sm:py-2 px-1 sm:px-2 font-medium">{trade.instrument || trade.symbol}</td>
                    <td className={`py-1.5 sm:py-2 px-1 sm:px-2 ${trade.direction === 'BUY' ? 'text-success' : 'text-destructive'}`}>
                      {trade.direction}
                    </td>
                    <td className="py-1.5 sm:py-2 px-1 sm:px-2 text-right hidden sm:table-cell">{trade.lot_size || trade.volume}</td>
                    <td className="py-1.5 sm:py-2 px-1 sm:px-2 text-right hidden sm:table-cell">{trade.entry_price}</td>
                    <td className="py-1.5 sm:py-2 px-1 sm:px-2 text-right hidden sm:table-cell">{trade.exit_price || trade.close_price || '-'}</td>
                    <td className={`py-1.5 sm:py-2 px-1 sm:px-2 text-right font-medium ${getPnLColor(trade.actual_profit_loss || trade.profit_loss)}`}>
                      {formatPnL(trade.actual_profit_loss || trade.profit_loss)}
                    </td>
                    <td className="py-1.5 sm:py-2 px-1 sm:px-2 text-right text-textSecondary hidden sm:table-cell">
                      {formatDate(trade.closed_at || trade.close_time || trade.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Widget>
  );
};

ClosedPositionsWidget.propTypes = {
  hideTitle: PropTypes.bool,
  accountId: PropTypes.string,
  limit: PropTypes.number,
};

export default ClosedPositionsWidget;
